import { Component, Input, OnChanges, OnDestroy, EventEmitter, OnInit, SimpleChanges, ViewChild, ApplicationRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { CollectFromFormComponent } from './collect-from-form/collect-from-form.component';
import { DeliveryToFormComponent } from './delivery-to-form/delivery-to-form.component';
import { OrderDetailFormComponent } from './order-detail-form/order-detail-form.component';
import { PalletInformationFormComponent } from './pallet-information-form/pallet-information-form.component';
import { ProductDetailFormComponent } from './product-detail-form/product-detail-form.component';
import { OrderDocumentsFormComponent } from './order-documents-form/order-documents-form.component';
import { NgbModal, NgbTypeahead, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subject, catchError, switchMap, take, takeUntil, tap } from 'rxjs';
import { FreightPayer } from '../../../../../../shared/freight-payers/models/FreightPayer';
import { AsyncPipe, JsonPipe, NgClass } from '@angular/common';
import { IAppState } from '../../../../../app.reducer';
import { Store } from '@ngrx/store';
import { selectDangerousGoodsCodes } from '../../../../../../shared/products/products.reducer';
import { loadDangerousGoodsCodes } from '../../../../../../shared/products/products.actions';
import { DangerousGoodsCode } from '../../../../../../shared/products/models/DangerousGoodsCode';
import { MasterContactAddress } from '../../../../../../shared/master-contact/models/MasterContactAddress';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToasterService } from '../../../../../../shared/toaster/toaster.service';
import { MogoDropzoneComponent } from "../../../../../../shared/mogo-dropzone/mogo-dropzone.component";
import dayjs from 'dayjs';
import { OrderService } from '../../../../../../shared/orders/order.service';
import { OrderData } from '../../../../../../shared/orders/models/OrderData';
import { OrderPalletLabelModalComponent } from '../../../../../../shared/modals/order-pallet-label-modal/order-pallet-label-modal.component';
import { ProofOfDeliveryPdfService } from '../../../../../../shared/proof-of-delivery/proof-of-delivery-pdf.service';
import { BlobStorageService } from '../../../../../../shared/blob-storage/blob-storage.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FormDefaultsService } from '../../../../../../shared/form-defaults/form-defaults.service';
import { DefaultType } from '../../../../../../shared/form-defaults/models/DefaultType';
import { wholeNumberValidator } from '../../../../../../shared/validators/whole-number.validator';
import { atLeastOneRequiredValidator } from '../../../../../../shared/validators/at-least-one-required.validator';
import { productCategorySelectedValidator } from '../../../../../../shared/validators/product-category-selected.validator';
import { productDescriptionSelectedValidator } from '../../../../../../shared/validators/product-description-selected.validator';
import { productDescriptionLengthValidator } from '../../../../../../shared/validators/product-description-length-validator';
import { AttachmentService } from '../../../../../../shared/attachments/attachment.service';
import { AttachmentEntityType } from '../../../../../../shared/attachments/enum/AttachmentEntityType';

@Component({
  selector: 'app-order-upsert-form',
  standalone: true,
  imports: [
    CollectFromFormComponent,
    DeliveryToFormComponent,
    OrderDetailFormComponent,
    PalletInformationFormComponent,
    ProductDetailFormComponent,
    OrderDetailFormComponent,
    OrderDocumentsFormComponent,
    NgbTypeaheadModule,
    FormsModule,
    ReactiveFormsModule,
    JsonPipe,
    NgClass,
    AsyncPipe,
    NgSelectModule,
    MogoDropzoneComponent
  ],
  templateUrl: './order-upsert-form.component.html',
  styleUrls: ['./order-upsert-form.component.scss']

})
export class OrderUpsertFormComponent implements OnInit, OnChanges, OnDestroy {

  formGroup!: FormGroup;
  searchModel: any;

  disableEmitter = new EventEmitter<boolean>();
  private readonly defaultsCancelled$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();

  @ViewChild('freightPayerInput', { static: true }) instance!: NgbTypeahead;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  dangerousProductCodes$!: Observable<DangerousGoodsCode[]>;
  masterContactAddresses$!: Observable<MasterContactAddress[]>;

  orderCreatedSuccessMode: boolean = false;

  mostRecentSavedOrder: any = null;

  @Input()
  freightPayers: FreightPayer[] | null = [];

  @Input()
  orderId: number | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly store$: Store<IAppState>,
    private readonly router: Router,
    private readonly orderService: OrderService,
    private readonly formDefaultService: FormDefaultsService,
    private readonly toasterService: ToasterService,
    private readonly modalService: NgbModal,
    private readonly proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private readonly blobStorageService: BlobStorageService,
    private readonly appRef: ApplicationRef,
    private readonly attachmentService: AttachmentService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['freightPayers']) {
      if (this.freightPayers?.length === 1) {
        this.formGroup?.get('freightPayer')?.patchValue(this.freightPayers?.[0]?.clienteleId);
      }
      this.formGroup?.updateValueAndValidity();
    }
  }

  ngOnInit() {
    this.formGroup = this.formBuilder.group({
      freightPayer: this.formBuilder.control(null, [Validators.required]),
    }, { validators: (form: AbstractControl) => this.validateAssetInformation(form) });

    this.store$.dispatch(loadDangerousGoodsCodes());
    this.dangerousProductCodes$ = this.store$.select(selectDangerousGoodsCodes);

    if (this.orderId) {
      this.patchFormGroupForUpdate(this.orderId);
    };

    this.formGroup.get('freightPayer')!.valueChanges.pipe(
      switchMap((value: number | null) => {
        if (this.orderId !== null || value === null) {
          return EMPTY;
        }
        // Read the controls directly: the parent's cached value has not yet
        // been updated when the freight payer control emits.
        const formAtRequest = this.defaultsSnapshot();
        return this.formDefaultService.getFormDefaults(value, DefaultType.GeneralFreight).pipe(
          take(1),
          takeUntil(this.defaultsCancelled$),
          tap((orderDefault) => {
            if (this.defaultsSnapshot() !== formAtRequest) {
              return;
            }
            if (orderDefault && orderDefault.formValueJson !== null && orderDefault.formValueJson !== "") {
              const formValue = JSON.parse(orderDefault.formValueJson);
              const allMyProducts = formValue.productDetail.products.map((product: any) => ({ ...product }));

              this.formGroup.patchValue(formValue, { emitEvent: false });
              this.formGroup.patchValue({
                collectFrom: {
                  ...formValue.collectFrom,
                  pickupOpen: {
                    date: {},
                    time: { hour: 12, minute: 0 }
                  },
                  pickupClose: {
                    date: {},
                    time: { hour: 12, minute: 0 }
                  },
                },
                deliverTo: {
                  ...formValue.deliverTo,
                  deliverDateTime: {
                    date: {
                      year: dayjs().year(),
                      month: dayjs().month() + 1,
                      day: dayjs().date()
                    },
                    time: { hour: 12, minute: 0 }
                  }
                },
                orderDocuments: {
                  documents: []
                }
              });

              if (allMyProducts.length === 0) {
                (<FormArray>this.formGroup.get('productDetail.products')).clear();
                (<FormArray>this.formGroup.get('productDetail.products')).push(this.formBuilder.group({
                  category: this.formBuilder.control({
                    name: null,
                    productCategoryId: null
                  }, [productCategorySelectedValidator()]),
                  description: this.formBuilder.control({
                    name: null,
                    productId: null
                  }, [productDescriptionSelectedValidator(), productDescriptionLengthValidator()]),
                  quantity: this.formBuilder.control(1, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3']), wholeNumberValidator(), Validators.min(1)]),
                  weightKg: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
                  volumeM3: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
                  dangerousGoodsCode: this.formBuilder.control(null),
                }));
              } else {
                this.appRef.tick();
                this.patchProducts(allMyProducts);
              }

            } else {
              this.clearForm(true);
            }
          }),
          catchError(() => {
            this.toasterService.showDanger('Unable to load saved defaults. Please complete the order details manually.');
            return EMPTY;
          })
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  private validateAssetInformation(form: AbstractControl): ValidationErrors | null {
    const freightPayerId = form.get('freightPayer')!.value;
    if (freightPayerId === null) {
      return null;
    }

    const freightPayer = this.freightPayers?.find(payer => payer.clienteleId === freightPayerId);
    if (freightPayer === undefined || typeof freightPayer.requireAssetInfo !== 'boolean') {
      return { freightPayerSettingsUnavailable: true };
    }

    if (!freightPayer.requireAssetInfo) {
      return null;
    }

    const palletInformation = form.get('palletInformation');
    const hasPallets = ['chepPallets', 'loscamPallets', 'greenPallets', 'otherPallets'].some(field => {
      const quantity = palletInformation?.get(field)?.value;
      return typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 0;
    });

    return hasPallets ? null : { assetInformationRequired: true };
  }

  private defaultsSnapshot(): string {
    return JSON.stringify(this.formGroup.getRawValue(), (key, value) => {
      // Contact lookups normalize address metadata without changing the selection.
      if (key === 'masterContactAddressId' && value !== null && typeof value === 'object') {
        return value.masterContactId;
      }
      return value;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.defaultsCancelled$.complete();
  }

  private patchFormGroupForUpdate(orderId: number) {
    this.orderService.getOrder(orderId).subscribe((order: OrderData) => {
      const orderProductForm = order.orderProducts.map((orderProduct: any) => {
        return {
          category: {
            name: orderProduct.productCategoryName,
            productCategoryId: orderProduct.productCategoryId
          },
          description: {
            name: orderProduct.productName,
            productId: orderProduct.productId,
          },
          quantity: orderProduct.quantity,
          weightKg: orderProduct.weightKg,
          volumeM3: orderProduct.volumeM3,
          dangerousGoodsCode: orderProduct.dangerousGoodClassId,
        };
      });

      //trigger freightpayer change
      this.formGroup.patchValue(
        {
          freightPayer: order.freightPayerId,
        },
        { emitEvent: true }
      )

      this.formGroup.patchValue(
        {
          orderDetail: {
            customerReference: order.customerReference,
            exportOrder: order.exportOrder,
            exportCertificateNumber: order.exportCertificateNumber,
            orderNumber: order.orderNumber,
            requestTruck: order.requestTruck,
            temperature: order.temperature,
            temperatureType: order.temperatureType,
          },
          palletInformation: order.palletInformation,
          productDetail: {
            products: orderProductForm,
          },
        },
        { emitEvent: false }
      );

      this.populateOrderDocumentForm(order.orderAttachments, false);

      let collectFromData: any = {
        addressSelection: {
          masterContactAddressId: {
            masterContactId: order.fromMasterContactId,
            masterContactName: "",
          }
        },
        siteContactId : order.siteOrderContact ? order.siteOrderContact.orderContactId : 0,
        siteContact : order.siteOrderContact ? order.siteOrderContact.siteContact : null,
        siteContactPhone : order.siteOrderContact ? order.siteOrderContact.siteContactPhone : null,
        siteContactEmail : order.siteOrderContact ? order.siteOrderContact.siteContactEmail : null
      };

      if (order.pickupOpen) {
        const pickUpOpenDate = new Date(order.pickupOpen);
        collectFromData.pickupOpen = {
          date: {
            year: pickUpOpenDate.getFullYear(),
            month: pickUpOpenDate.getMonth() + 1,
            day: pickUpOpenDate.getDate(),
          },
          time: {
            hour: pickUpOpenDate.getHours(),
            minute: pickUpOpenDate.getMinutes(),
          },
        }
      }

      if (order.pickupClose) {
        const pickUpCloseDate = new Date(order.pickupClose);
        collectFromData.pickupClose = {
          date: {
            year: pickUpCloseDate.getFullYear(),
            month: pickUpCloseDate.getMonth() + 1,
            day: pickUpCloseDate.getDate(),
          },
          time: {
            hour: pickUpCloseDate.getHours(),
            minute: pickUpCloseDate.getMinutes(),
          },
        };
      }

      const deliveryDate = new Date(order.deliveryDate);
      this.formGroup.patchValue({
        collectFrom: collectFromData,
        deliverTo: {
          addressSelection: {
            masterContactAddressId: {
              masterContactId: order.toMasterContactId,
              masterContactName: "",
            }
          },
          siteContactId : order.customerOrderContact ? order.customerOrderContact.orderContactId : null,
          siteContact : order.customerOrderContact ? order.customerOrderContact.siteContact : null,
          siteContactPhone : order.customerOrderContact ? order.customerOrderContact.siteContactPhone : null,
          siteContactEmail : order.customerOrderContact ? order.customerOrderContact.siteContactEmail : null,
          instructions: order.despatchComments,
          deliverDateTime: {
            date: {
              year: deliveryDate.getFullYear(),
              month: deliveryDate.getMonth() + 1,
              day: deliveryDate.getDate(),
            },
            time: {
              hour: deliveryDate.getHours(),
              minute: deliveryDate.getMinutes(),
            },
          }
        }
      });
      this.appRef.tick();

      this.formGroup.patchValue({
        collectFrom: {
          preciseLocationOptions: {
            deliveryLocationOverrideId: order.collectFromDeliveryLocationOverrideId
          }
        },
        deliverTo: {
          preciseLocationOptions: {
            deliveryLocationOverrideId: order.deliverToDeliveryLocationOverrideId
          }
        }
      });

      this.appRef.tick();

      this.patchProducts(orderProductForm);
    });

    this.formGroup.get('freightPayer')?.disable();
  }

  private populateOrderDocumentForm(orderAttachments: any[], emitEvent: boolean = true) {
    let orderFiles: File[] = [];

    this.attachmentService.getAttachmentsListForEntity(this.orderId!, AttachmentEntityType[AttachmentEntityType.Order], "")
      .subscribe((result) => {
        const attachments = result.filter(x => x.attachmentTypeCode === 'Attachment');

        attachments.forEach((response, index) => {
          const file = this.blobStorageService.base64ToFile(response.content, response.attachmentName, response.contentType);

          if (file) {
            orderFiles.push(file);
          }
        });

        if (orderFiles) {
          this.formGroup.patchValue({
            orderDocuments: {
              documents: orderFiles
            }
          }, { emitEvent });
        }
      });
  }

  private patchProducts(products: any) {
    (<FormArray>this.formGroup.get('productDetail.products')).clear();

    this.appRef.tick();
    products.forEach((value: any) => {
      (<FormArray>this.formGroup.get('productDetail.products')).push(this.formBuilder.group({
        category: this.formBuilder.control({
          name: value.category?.name,
          productCategoryId: value.category?.productCategoryId
        }, [productCategorySelectedValidator()]),
        description: this.formBuilder.control({
          name: value.description?.name,
          productId: value.description?.productId,
        }, [productDescriptionSelectedValidator(), productDescriptionLengthValidator()]),
        quantity: this.formBuilder.control(value.quantity, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3']), wholeNumberValidator(), Validators.min(1)]),
        weightKg: this.formBuilder.control(value.weightKg, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        volumeM3: this.formBuilder.control(value.volumeM3, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        dangerousGoodsCode: this.formBuilder.control(value.dangerousGoodsCode),
      }), { emitEvent: false });
    });
    this.formGroup.get('productDetail.products')?.updateValueAndValidity();
  }

  clearForm(onlyChildren: boolean = false) {
    this.defaultsCancelled$.next();
    if (!onlyChildren) {
      if (this.orderId === null) {
        this.formGroup.reset();
      } else {
        // Do this so we dont clear the freight payer
        this.clearForm(true);
      }
    } else {
      this.formGroup.get('collectFrom')?.reset();
      this.formGroup.get('deliverTo')?.reset();
      this.formGroup.get('orderDetail')?.reset();
      this.formGroup.get('palletInformation')?.reset();
      this.formGroup.get('orderDocuments.documents')?.reset([]);
      (this.formGroup.get('productDetail.products') as FormArray).clear();
      this.appRef.tick();
      (<FormArray>this.formGroup.get('productDetail.products')).push(this.formBuilder.group({
        category: this.formBuilder.control({
          name: null,
          productCategoryId: null
        }, [productCategorySelectedValidator()]),
        description: this.formBuilder.control({
          name: null,
          productId: null
        }, [productDescriptionSelectedValidator(), productDescriptionLengthValidator()]),
        quantity: this.formBuilder.control(1, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3']), wholeNumberValidator(), Validators.min(1)]),
        weightKg: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        volumeM3: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        dangerousGoodsCode: this.formBuilder.control(null),
      }));
    }
  }

  saveDefaults() {
    this.defaultsCancelled$.next();
    if (this.formGroup.get('freightPayer')?.invalid) {
      this.toasterService.show({ text: 'Please select a freight payer', delay: 5000, classname: 'bg-danger text-light' });
      return;
    }
    const clienteleId = this.formGroup.get('freightPayer')?.value;
    // Clear defaults on a snapshot so the displayed form and submission value stay in sync.
    const saveModel = this.formGroup.getRawValue();

    saveModel.collectFrom.pickupOpen = null;
    saveModel.collectFrom.pickupClose = null;
    saveModel.deliverTo.deliverDateTime = null;

    saveModel.orderDocuments.documents = [];

    this.formDefaultService.saveFormDefaults({
      clienteleId: clienteleId,
      formValueJson: JSON.stringify(saveModel),
      type: DefaultType.GeneralFreight,
      userId: ''
    }).pipe(take(1)).subscribe(response => {
      this.toasterService.showDefault('Order defaults saved successfully');
    });
  }

  clearDefaults() {
    this.defaultsCancelled$.next();
    if (this.formGroup.get('freightPayer')?.invalid) {
      this.toasterService.show({ text: 'Please select a freight payer', delay: 5000, classname: 'bg-danger text-light' });
      return;
    }
    const clienteleId = this.formGroup.get('freightPayer')?.value;
    this.formDefaultService.deleteFormDefaults(clienteleId, DefaultType.GeneralFreight).pipe(take(1)).subscribe(response => {
      this.toasterService.showDefault('Defaults cleared');
      this.clearForm(true);
    });
  }

  productsAreValid(): string {
    if (((this.formGroup.get('productDetail.products') as FormArray).length <= 0)) {
      return "Please add at least one product";
    }

    if ((this.formGroup.get('productDetail.products') as FormArray).controls.some(product => {
      return (product.get('quantity')?.value < 1);
    })) {
      return "Please ensure all product lines have a quantity of at least 1";
    }

    if ((this.formGroup.get('productDetail.products') as FormArray).controls.some(product => {
      return (product.get('category')?.invalid || product.get('description')?.invalid) ||
        ((product.get('quantity')?.value < 0) ||
          (product.get('weightKg')?.value < 0) ||
          (product.get('volumeM3')?.value < 0)) ||
        (product.get('description')?.value?.productId === null);
    })) {
      return "Please ensure all products have a valid category and description";
    }

    return "";
  }

  printLabels() {
    if (!this.orderCreatedSuccessMode || !this.mostRecentSavedOrder) {
      return;
    }

    const ref = this.modalService.open(OrderPalletLabelModalComponent);
    ref.componentInstance.order = this.mostRecentSavedOrder;
  }

  printConsignmentNote() {
    if (!this.orderCreatedSuccessMode || !this.mostRecentSavedOrder) {
      return;
    }

    this.proofOfDeliveryPdfService.printOrderConsignmentPdf(this.mostRecentSavedOrder.orderId).subscribe({
      next: (response) => {
        if (response) {
          this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
        }
      },
      error: (response: HttpErrorResponse) => {
        console.error(response.message, 'An error occurred:');
      }
    });
  }

  clearAndCreate() {
    this.formGroup.get('freightPayer')?.reset();

    this.clearForm(true);
    this.disableEmitter.emit(false);
    this.orderCreatedSuccessMode = false;
    this.mostRecentSavedOrder = null;
  }

  validateDates() {
    // Validate the submission value, which can differ from individual control values.
    const formValue = this.formGroup.value;
    const pickupOpen = formValue.collectFrom?.pickupOpen;
    const pickupClose = formValue.collectFrom?.pickupClose;
    const deliverDateTime = formValue.deliverTo?.deliverDateTime;

    if (!this.isValidDateTime(pickupOpen, false)) {
      this.toasterService.show({ text: 'Please provide a valid pickup open date and time', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    if (!this.isValidDateTime(pickupClose, false)) {
      this.toasterService.show({ text: 'Please provide a valid pickup close date and time', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    if (!this.isValidDateTime(deliverDateTime, true)) {
      this.toasterService.show({ text: 'Please provide a valid delivery date and time', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    return true;
  }

  private isValidDateTime(value: any, required: boolean): boolean {
    if (value == null) {
      return !required;
    }

    const date = value.date;
    if (date == null || (typeof date === 'object' && Object.keys(date).length === 0)) {
      return !required;
    }

    if (!Number.isInteger(date.year) || date.year < 1 || date.year > 9999 ||
        !Number.isInteger(date.month) || date.month < 1 || date.month > 12 ||
        !Number.isInteger(date.day) || date.day < 1) {
      return false;
    }

    const calendarDate = new Date(0);
    calendarDate.setFullYear(date.year, date.month - 1, date.day);
    if (calendarDate.getMonth() !== date.month - 1 || calendarDate.getDate() !== date.day) {
      return false;
    }

    const time = value.time;
    if (time == null || !Number.isInteger(time.hour) || time.hour < 0 || time.hour > 23 ||
        !Number.isInteger(time.minute) || time.minute < 0 || time.minute > 59) {
      return false;
    }

    return time.second == null || (Number.isInteger(time.second) && time.second >= 0 && time.second <= 59);
  }

  formInputsMeetCharLimits(): boolean {
    const orderNumber = this.formGroup.get('orderDetail.orderNumber')?.value;
    const customerReference = this.formGroup.get('orderDetail.customerReference')?.value;

    if (orderNumber && orderNumber.length > 50) {
      this.toasterService.show({ text: 'Order number exceeds character limit of 50', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    if (customerReference && customerReference.length > 50) {
      this.toasterService.show({ text: 'Customer reference exceeds character limit of 50', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }
    return true;
  }

  validateFromToAddress() {
    let valid = true;
    const collectFrom = this.formGroup.get('collectFrom')?.value;
    const deliverTo = this.formGroup.get('deliverTo')?.value;

    if (!collectFrom || !deliverTo || !collectFrom.addressSelection || !deliverTo.addressSelection) {
      this.toasterService.show({ text: 'Please provide both collection and delivery addresses', delay: 5000, classname: 'bg-danger text-light' });
      return false;
    }

    if (collectFrom.addressSelection?.isCreateMode) {
      if (collectFrom.addressSelection?.addressDetails === null ||
        collectFrom.addressSelection?.addressDetails.addressComponents === null ||
        collectFrom.addressSelection?.addressDetails.addressComponents.length === 0 ||
        !collectFrom.addressSelection?.addressName || collectFrom.addressSelection?.addressName.trim() === ""
      ) {
        this.toasterService.show({ text: 'Please provide a collection address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    } else {
      if (!collectFrom.addressSelection?.masterContactAddressId?.masterContactId) {
        this.toasterService.show({ text: 'Please select a collection address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    }

    if (deliverTo.addressSelection?.isCreateMode) {
      if (deliverTo.addressSelection?.addressDetails === null ||
        deliverTo.addressSelection?.addressDetails.addressComponents === null ||
        deliverTo.addressSelection?.addressDetails.addressComponents.length === 0 ||
        !deliverTo.addressSelection?.addressName || deliverTo.addressSelection?.addressName.trim() === ""
      ) {
        this.toasterService.show({ text: 'Please provide a delivery address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }

    } else {
      if (!deliverTo.addressSelection?.masterContactAddressId?.masterContactId) {
        this.toasterService.show({ text: 'Please select a delivery address', delay: 5000, classname: 'bg-danger text-light' });
        valid = false;
      }
    }

    return valid;
  }

  submit() {
    this.defaultsCancelled$.next();
    this.formGroup.markAllAsTouched();
    if (this.formGroup.hasError('freightPayerSettingsUnavailable')) {
      this.toasterService.showDanger('Freight payer settings are unavailable. Please wait or reload the page before saving.');
      return;
    }

    if (this.formGroup.hasError('assetInformationRequired')) {
      this.toasterService.showDanger('Asset information needs to be entered');
      return;
    }

    const productsValidMessage = this.productsAreValid();
    const charLimitsValid = this.formInputsMeetCharLimits();
    const fromTooAddressValid = this.validateFromToAddress();
    const datesValid = this.validateDates();

    if (productsValidMessage !== "" || !charLimitsValid || !fromTooAddressValid || !datesValid) {
      if (productsValidMessage !== "") {
        this.toasterService.show({ text: productsValidMessage, delay: 5000, classname: 'bg-danger text-light' });
      }
      return;
    }

    if (this.formGroup.valid) {
      if (this.orderId) {
        //edit
        this.orderService.updateOrder(this.formGroup.getRawValue(), this.orderId).subscribe({
          next: () => this.router.navigate(['app/order/home']),
          error: (error: HttpErrorResponse) => this.showSubmitError(error)
        });
      } else {
        // create
        this.orderService.createOrder({
          ...this.formGroup.value
        }).subscribe({
          next: (order: unknown) => {
            let neworder = order as OrderData;
            this.mostRecentSavedOrder = order;
            this.formGroup.get("orderDetail.orderNumber")!.setValue(neworder.orderNumber);
            this.orderCreatedSuccessMode = true;
            this.disableEmitter.emit(true);
            this.toasterService.show({
              text: `Order ${neworder.orderNumber} created successfully`, delay: 5000, classname: 'bg-success text-light'
            });
          },
          error: (error: HttpErrorResponse) => this.showSubmitError(error)
        });
      }
    } else {
      this.toasterService.show({ text: 'Fix validation errors before continuing', delay: 5000, classname: 'bg-danger text-light' });
    }
  }

  private showSubmitError(error: HttpErrorResponse) {
    let message = 'Unable to save the order. Please try again. If the issue persists, contact support.';
    if (error.status === 400 && error.error && typeof error.error.message === 'string' && error.error.message.trim() !== '') {
      message = error.error.message;
    }
    this.toasterService.showDanger(message);
  }
}
